import {
  APP_TIERS,
  BUGSNAG_API_KEY_PATTERN,
  isAppTier,
  resolveMonitoringKey,
  type AppTier,
} from '@point2-inc/constants';

/**
 * Web's monitoring environment.
 *
 * Deliberately not zod, unlike Admin's `src/shared/config/env.ts`. This site's
 * other configuration is read lazily through plain functions in
 * `lib/trip-share/config.ts`, and a schema that throws at import time would
 * take the marketing pages down over a monitoring variable. The rule here is
 * the inverse of the API's: telemetry configuration must never be able to stop
 * the site rendering.
 *
 * NEXT_PUBLIC_* values are substituted into the browser bundle at BUILD time.
 * Changing one on Vercel needs a redeploy, not a restart; a Preview build
 * carries whatever the Preview environment held when it was built.
 *
 * `process.env.NEXT_PUBLIC_*` is written out in full on purpose — Next
 * substitutes these by literal text match, so a computed lookup such as
 * `process.env[name]` is replaced by nothing at all in the browser bundle.
 */

export interface MonitoringEnv {
  tier: AppTier;
  apiKey?: string;
  enabled: boolean;
  /** Why reporting is off, when it is. One line, safe to log. */
  disabledReason?: string;
  /** The release identifier events and source-map uploads must agree on. */
  appVersion: string;
}

export const resolveMonitoringEnv = (input: {
  tier?: string;
  apiKey?: string;
  killSwitch?: string;
  appVersion?: string;
}): MonitoringEnv => {
  // An unrecognised tier resolves to `local` rather than being guessed at, so a
  // typo files its events under the stage nobody pages on.
  const tier: AppTier = isAppTier(input.tier) ? input.tier : 'local';
  const killed = input.killSwitch === 'false';
  const resolved = resolveMonitoringKey(input.apiKey);

  return {
    tier,
    ...(resolved.key ? { apiKey: resolved.key } : {}),
    enabled: !killed && Boolean(resolved.key),
    ...(killed
      ? { disabledReason: 'NEXT_PUBLIC_BUGSNAG_ENABLED=false' }
      : resolved.message
        ? { disabledReason: resolved.message }
        : {}),
    appVersion: input.appVersion?.trim() || '0.0.0-dev',
  };
};

export const monitoringEnv: MonitoringEnv = resolveMonitoringEnv({
  tier: process.env.NEXT_PUBLIC_APP_ENV,
  apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY,
  killSwitch: process.env.NEXT_PUBLIC_BUGSNAG_ENABLED,
  appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
});

export { APP_TIERS, BUGSNAG_API_KEY_PATTERN };
